import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { authApi } from '../../api/auth';
import { useTranslation } from 'react-i18next';
import { errorText } from '../../i18n/errors';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const token = params.get('token') ?? '';

  const reset = useMutation({
    mutationFn: () => authApi.resetPassword(token, password),
    onSuccess: () => navigate('/login', { replace: true }),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    reset.mutate();
  };

  if (!token) {
    return (
      <div className="flex flex-col gap-5">
        <h1 className="text-section-title font-semibold">{t('auth.reset.invalid')}</h1>
        <p className="text-body text-content-secondary">{t('auth.reset.invalidHint')}</p>
        <Link to="/forgot-password" className="text-meta text-accent hover:text-accent-hover">{t('auth.reset.request')}</Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <h1 className="text-section-title font-semibold">{t('auth.reset.title')}</h1>

      {reset.isError ? <Alert tone="error">{errorText(reset.error)}</Alert> : null}

      <Input
        label={t('auth.reset.title')}
        name="password"
        type="password"
        autoComplete="new-password"
        required
        minLength={8}
        hint={t('auth.field.passwordHint')}
        value={password}
        onChange={(event) => setPassword(event.target.value)}
      />

      <Button type="submit" variant="primary" disabled={reset.isPending}>
        {reset.isPending ? t('common.saving') : t('auth.reset.submit')}
      </Button>
    </form>
  );
}
